// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import moment from 'moment';
import { VNode } from 'snabbdom';
import { jsx } from 'panel';
import { CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT } from 'common/constants';
import { reporter } from 'common/report/error-reporting';
import { CurrentProject } from 'common/report/model-resources/projects';
import { colors } from 'common/util/colors';
import { getCurrentMoment, getTimeString, parseDate, parseTimestamp } from 'common/util/date';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { Translatable } from 'common/i18n';
import { LocalizationStore } from 'common/i18n/store';
function getAbbreviatedTimezoneAsOf(date: moment.Moment, timezone: string | null): Translatable | null {
  try {
    return _ref => {
      var _parts$find$value, _parts$find;
      let {
        formatDateToParts
      } = _ref;
      const parts = formatDateToParts(date, {
        timeZoneName: "short",
        timeZone: timezone
      });

      // DateTimeFormat can't return timezone name only, so we format to parts and then pull it out
      return (_parts$find$value = (_parts$find = parts.find(part => part.type === "timeZoneName")) === null || _parts$find === void 0 ? void 0 : _parts$find.value) !== null && _parts$find$value !== void 0 ? _parts$find$value : "";
    };
  } catch (err) {
    // report the error but don't die because we couldn't parse a timezone
    reporter.error(err);
    return null;
  }
}
function getProjectAsOfDate(currentProject: CurrentProject | undefined): null | moment.Moment {
  if (currentProject !== null && currentProject !== void 0 && currentProject.projectAsOfDate) {
    const parsedDate = parseDate(currentProject === null || currentProject === void 0 ? void 0 : currentProject.projectAsOfDate);
    if (!parsedDate) {
      throw new Error("\"projectAsOfDate\" does not contain a valid date string");
    }
    return moment(parsedDate);
  }
  return null;
}
export function getDateRangeTooltipText(dateRange: DateRange, currentProject: CurrentProject | undefined): [Translatable, Translatable] {
  var _getProjectAsOfDate;
  const dateFormat = dateRange.hasSubDayGranularity() ? TIMESTAMP_FORMAT : CALENDAR_DATE_FORMAT;
  const asOfDate = (_getProjectAsOfDate = getProjectAsOfDate(currentProject)) !== null && _getProjectAsOfDate !== void 0 ? _getProjectAsOfDate : getCurrentMoment({
    timezone: currentProject === null || currentProject === void 0 ? void 0 : currentProject.timezone
  });
  const {
    from,
    to
  } = dateRange.getCalendarDatesAsOf(asOfDate.format(dateFormat), dateFormat, null, currentProject === null || currentProject === void 0 ? void 0 : currentProject.timePeriodSettings);
  const toDate = moment(parseDate(to));
  let fromDate = moment(parseDate(from));

  // for cases where `from` is not defined and asOfDate is past toDate
  if (fromDate.isAfter(toDate)) {
    fromDate = toDate;
  }
  const dateRangeUnit = dateRange.hasSubDayGranularity() ? "minute" : "day";
  const isSameDate = fromDate.isSame(toDate, dateRangeUnit);
  let fromTimeText = "";
  let toTimeText = "";
  if (dateRange.hasSubDayGranularity()) {
    fromTimeText = " ".concat(getTimeString(parseTimestamp(from)));
    toTimeText = " ".concat(getTimeString(parseTimestamp(to)));
  }
  const dateRangeText: Translatable = _ref2 => {
    let {
      formatDate
    } = _ref2;
    const formattedFromDate = formatDate(fromDate, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
    const formattedToDate = formatDate(toDate, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
    return isSameDate || !toDate.isValid() ? "".concat(formattedFromDate).concat(fromTimeText) : "".concat(formattedFromDate).concat(fromTimeText, " - ").concat(formattedToDate).concat(toTimeText);
  };
  const timezoneText = getAbbreviatedTimezoneAsOf(toDate, currentProject === null || currentProject === void 0 ? void 0 : currentProject.timezone);
  return [dateRangeText, timezoneText];
}
export function getDateRangeTooltipRenderer(dateRange: DateRange, currentProject: CurrentProject): (i18n: LocalizationStore) => VNode {
  // lazy load this date info because although the date parsing logic is not noticeably slow by itself (maybe ~5ms),
  // it adds up when there's multiple tooltip elements on the page.
  return _ref3 => {
    let {
      print
    } = _ref3;
    const projectAsOfDate = getProjectAsOfDate(currentProject);
    const [dateRangeText, timezoneText] = getDateRangeTooltipText(dateRange, currentProject);
    return <div style={{
      'text-align': "center"
    }}>
        <div>
          <span>{print(dateRangeText)}</span>
          &nbsp;
          {currentProject ? <span style={{
          color: colors.gray3
        }}>{"(".concat(print(timezoneText), ")")}</span> : null}
        </div>
        {projectAsOfDate ? <div> Data last imported {projectAsOfDate.format("MMM D, YYYY")}</div> : null}
      </div>;
  };
}